import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  DOCUMENT,
  ElementRef,
  PLATFORM_ID,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { combineLatest, debounceTime, distinctUntilChanged, filter, switchMap, tap, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AlertService,
  DeviceService,
  DialogService,
  IconComponent,
  LoadingComponent,
  SingleSelectGroupComponent,
  SingleSelectComponent,
} from '@gains-com/ui-angular';
import { CarouselComponent } from '../../components/carousel/carousel.component.js';
import { SlideComponent } from '../../components/carousel/slide/slide.component.js';
import { AnalyticsDirective } from '../../core/analytics/analytics.directive.js';
import { UserViewService } from '../../core/view-states/user-view.service.js';
import { AuthClickDirective } from '../../directives/auth-click.directive.js';
import { GameCardComponent } from '../../components/game-card/game-card.component.js';
import { ImageLoaderComponent } from '../../components/image-loader/image-loader.component.js';
import { GameService } from '../../core/game.service.js';
import { WalletDialogService } from '../../core/wallet-dialog.service.js';
import { MerchDialogService } from '../../core/merch-dialog.service.js';
import { EnvironmentPipe } from '../../pipes/environment.pipe.js';
import { AuthService } from '../../core/auth.service.js';
import { AuthComponent, AuthDialogData } from '../../auth/auth.component.js';
import { DialogKey } from '../../shared/dialog-keys.js';
import { LocalStorage } from '../../core/local-storage/local-storage.service.js';
import { LocalStorageKey } from '../../core/local-storage/local-storage-keys.js';
import { NoPurchaseNecessaryDialogComponent } from '../../components/no-purchase-necessary-dialog/no-purchase-necessary-dialog.component.js';
import { MetadataViewService } from '../../core/view-states/metadata-view.service.js';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Seo } from '../../core/seo.service.js';
import { SelfApiService } from '../../core/self-api.service.js';
import { MatFormField, MatInput, MatPrefix } from '@angular/material/input';
import { CasinoSettingKeyId, GameCollectionType, HeroCard } from '@gains-com/shared';
import { HeroCardsService } from '../../core/services/hero-cards.service.js';
import { WindowProvider } from '../../core/window.provider.js';
import { AppInstallService, DOWNLOAD_APP_DEEP_LINK_ACN } from '../../core/app-install.service.js';
import { environment } from '../../../environments/environment.js';
import { MadeInTheUsDialogComponent } from '../../components/made-in-the-us-dialog/made-in-the-us-dialog.component.js';
import { SearchGamesDialogComponent } from '../../components/search-games-dialog/search-games-dialog.component.js';
import { GameCollectionService } from '../../core/services/game-collection.service.js';
import { DeviceModeViewService } from '../../core/view-states/device-mode-view.service.js';
import { filterHeroCards } from './hero-card-visibility.js';

const PILL_SCROLL_THRESHOLD_PX = 20;

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  imports: [
    MatButton,
    RouterLink,
    CarouselComponent,
    SlideComponent,
    AuthClickDirective,
    IconComponent,
    LoadingComponent,
    GameCardComponent,
    ImageLoaderComponent,
    EnvironmentPipe,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatPrefix,
    AnalyticsDirective,
    SingleSelectGroupComponent,
    SingleSelectComponent,
  ],
  host: {
    class: 'content',
  },
})
export class HomeComponent implements OnInit {
  private static readonly COLLECTION_FRAGMENT_PREFIX = 'collection-';
  private static readonly RECENTLY_PLAYED_COLLECTION_ID = 'recently-played';
  private static readonly NEW_GAMES_COLLECTION_ID = 'new-games';

  private readonly _route = inject(ActivatedRoute);
  private readonly _dialog = inject(DialogService);
  private readonly _userView = inject(UserViewService);
  private readonly _auth = inject(AuthService);
  private readonly _gameService = inject(GameService);
  private readonly _selfApi = inject(SelfApiService);
  private readonly _localStorage = inject(LocalStorage);
  private readonly _router = inject(Router);
  private readonly _metadataView = inject(MetadataViewService);
  private readonly _seo = inject(Seo);
  private readonly _heroCardsService = inject(HeroCardsService);
  private readonly _window = inject(WindowProvider);
  private readonly _appInstall = inject(AppInstallService);
  private readonly _gameCollectionService = inject(GameCollectionService);
  private readonly _deviceMode = inject(DeviceModeViewService);
  private readonly _document = inject(DOCUMENT);
  private readonly _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _alert = inject(AlertService);
  private _isScrollingProgrammatically = false;

  readonly walletDialog = inject(WalletDialogService);
  readonly merchDialog = inject(MerchDialogService);
  readonly device = inject(DeviceService);

  readonly quickNav = viewChild('quickNav', { read: ElementRef });

  /** Placeholder arrays used to render skeleton carousels while game collections load. */
  readonly skeletonCarousels = Array.from({ length: 5 }, (_, i) => i);
  readonly skeletonCards = Array.from({ length: 8 }, (_, i) => i);

  private _selectedCollectionId = signal<string | null>(null);
  private _pendingFragment = signal<string | null>(null);
  selectedCollectionId = computed(
    () => this._selectedCollectionId() ?? this.allGameCollections()?.[0]?.game_collection_id ?? null,
  );
  showHeroCardsLoading = signal(true);
  showHeroCards = toSignal(this._metadataView.casinoSettingValue$<boolean>(CasinoSettingKeyId.LobbyPagePromoCards));

  nameSearchFormControl = new FormControl('');

  // Mode-targeted content params. For the sweeps majority these never
  // change after the initial value, so each fetch below still runs
  // exactly once; merch / gold-coins devices refetch once /self resolves.
  private readonly _merchCatalog$ = toObservable(this._deviceMode.merchCatalog).pipe(distinctUntilChanged());
  private readonly _contentMode$ = toObservable(this._deviceMode.contentMode).pipe(distinctUntilChanged());

  private readonly _heroCardsForUser = toSignal(
    combineLatest([
      this._userView.state$.pipe(distinctUntilChanged((u1, u2) => u1?.user_id === u2?.user_id)),
      this._contentMode$,
    ]).pipe(
      tap(() => this.showHeroCardsLoading.set(true)),
      switchMap(([user, mode]) => this._heroCardsService.listHeroCards(mode).pipe(map((cards) => ({ cards, user })))),
      tap(() => this.showHeroCardsLoading.set(false)),
    ),
  );

  // The device mode resolves in the browser after hydration (SSR never knows
  // this device's mode), so the merch gate is a computed over the fetched set
  // rather than a filter inside the fetch pipeline — a late-resolving merch
  // mode re-filters without a refetch.
  heroCards = computed(() => {
    const loaded = this._heroCardsForUser();
    if (!loaded) {
      return undefined;
    }
    return filterHeroCards(loaded.cards, {
      hasHadPurchase: !!loaded.user?.has_had_purchase,
      hasHadMerchPurchase: !!loaded.user?.has_had_merch_purchase,
      merchExperience: this._deviceMode.merchExperience(),
    });
  });

  onHeroCardClick(card: HeroCard) {
    if (!card.action_ref) {
      return;
    }

    if (card.action_ref.startsWith('#' + HomeComponent.COLLECTION_FRAGMENT_PREFIX)) {
      const collectionId = card.action_ref.slice(('#' + HomeComponent.COLLECTION_FRAGMENT_PREFIX).length);
      if (this.allGameCollections()?.some((c) => c.game_collection_id === collectionId)) {
        this.scrollToCollection(collectionId);
      }
      return;
    }

    if (card.action_ref.startsWith('/') || card.action_ref.startsWith(environment.webUrl)) {
      const path = card.action_ref.startsWith(environment.webUrl)
        ? card.action_ref.split(environment.webUrl)[1]
        : card.action_ref;
      this._router.navigateByUrl(path).then();
    } else if (card.action_ref.startsWith('https://')) {
      this._window.open(card.action_ref, '_blank');
    } else {
      switch (card.action_ref) {
        case 'open_daily_claim':
          this.walletDialog.openDailyClaim().then();
          break;
        case 'open_purchases':
          this.walletDialog.openPurchaseModal().then();
          break;
        case 'open_made_in_usa':
          this._dialog.open(MadeInTheUsDialogComponent);
          break;
        case 'open_no_purchase_necessary':
          this.openNoPurchaseNecessary();
          break;
        case 'open_download_app':
          this._appInstall.promptInstall();
          break;
      }
    }
  }

  private _baseGameCollections = toSignal(
    this._merchCatalog$.pipe(switchMap((isMerch) => this._gameCollectionService.listGameCollections$(isMerch))),
  );

  recentlyPlayedGames = toSignal(
    this._auth.verifiedUser$.pipe(
      switchMap((user) => {
        return user ? this._selfApi.getRecentlyPlayedGames$() : of(null);
      }),
    ),
  );

  newGames = toSignal(
    this._merchCatalog$.pipe(switchMap((isMerch) => this._gameService.getRecentlyAddedGames$(20, isMerch))),
  );

  allGameCollections = computed(() => {
    const baseCollections = [...(this._baseGameCollections() ?? [])].filter(
      (c) => c.analytics_key !== 'recently_played' && c.analytics_key !== 'new_games',
    );

    if (baseCollections.length && this.recentlyPlayedGames()?.length) {
      baseCollections.splice(0, 0, {
        game_collection_id: HomeComponent.RECENTLY_PLAYED_COLLECTION_ID,
        type: GameCollectionType.Custom,
        title: 'Recently Played',
        icon_url: `${environment.cdnUrl}/icons/hr/clock.svg`,
        sort_rank: 2,
        active: true,
        tag: null,
        game_provider_id: null,
        view_all_link: null,
        created_at: null,
        archived_at: null,
        analytics_key: 'recently_played',
        is_merch: this._deviceMode.merchCatalog(),
        games: this.recentlyPlayedGames(),
      });
    }

    if (baseCollections.length && this.newGames()?.length) {
      baseCollections.push({
        game_collection_id: HomeComponent.NEW_GAMES_COLLECTION_ID,
        type: GameCollectionType.Custom,
        title: 'New Games',
        icon_url: `${environment.cdnUrl}/icons/hr/star.svg`,
        sort_rank: 100,
        active: true,
        tag: null,
        game_provider_id: null,
        view_all_link: null,
        created_at: null,
        archived_at: null,
        analytics_key: 'new_games',
        is_merch: this._deviceMode.merchCatalog(),
        games: this.newGames(),
      });
    }

    return baseCollections;
  });

  private _updateUrlHash(collectionId: string | null) {
    if (!this._window) return;
    const base = this._window.location.pathname + this._window.location.search;
    this._window.history.replaceState(
      null,
      '',
      collectionId ? base + '#' + HomeComponent.COLLECTION_FRAGMENT_PREFIX + collectionId : base,
    );
  }

  scrollToCollection(collectionId: string) {
    this._selectedCollectionId.set(collectionId);
    this._updateUrlHash(collectionId);
    this._isScrollingProgrammatically = true;

    const element = this._document.getElementById(HomeComponent.COLLECTION_FRAGMENT_PREFIX + collectionId);
    if (!element) return;

    const scrollContainer = this._document.querySelector('.sidenav-content-container');
    if (!scrollContainer) return;

    const stickyNav = this._document.querySelector('.sticky-nav');
    const stickyHeight = stickyNav ? stickyNav.getBoundingClientRect().height : 0;
    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const offset = elementRect.top - containerRect.top + scrollContainer.scrollTop - stickyHeight;

    scrollContainer.scrollTo({ top: offset, behavior: 'smooth' });

    const timeout = setTimeout(() => {
      this._isScrollingProgrammatically = false;
    }, 1500);

    scrollContainer.addEventListener(
      'scrollend',
      () => {
        clearTimeout(timeout);
        this._isScrollingProgrammatically = false;
      },
      { once: true },
    );
  }

  // Providers list fetched from API, sorted by sort_rank
  providers = toSignal(
    this._merchCatalog$.pipe(switchMap((isMerch) => this._gameService.getGameProviders$(isMerch))),
    { initialValue: [] },
  );
  lobbyTitle = toSignal(
    this._userView.state$.pipe(
      map((state) => {
        let title = !state?.first_name ? 'Welcome' : '👋 Hey';
        if (state?.first_name) {
          title += `, ${state.first_name}`;
        } else {
          title += ' to Gains.com';
        }

        return title;
      }),
    ),
  );

  constructor() {
    this._seo.setTitle();

    if (this._route.snapshot.queryParams['ref']) {
      this._seo.setDescription(
        'Use my referral code and get up to 250 SC FREE! Play on Gains.com, the U.S. based sweepstakes social casino redefining online gaming with innovation, trust, compliance, and top rewards for players.',
      );
      this._seo.setImage('https://storage.googleapis.com/cdn.ripabet.com/seo/referral.webp');
    } else {
      this._seo.setDescription(
        'Play on Gains.com, the U.S. based sweepstakes social casino redefining online gaming with innovation, trust, compliance, and top rewards for players.',
      );
      this._seo.setImage('https://storage.googleapis.com/cdn.ripabet-staging.com/seo/lobby.webp');
    }

    this.nameSearchFormControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((nameSearch) => {
        if (nameSearch) {
          this._dialog.open(SearchGamesDialogComponent, { data: nameSearch });
          this.nameSearchFormControl.setValue('');
        }
      });

    // An `acn` that arrives while the lobby is already active (a legacy `/shop`
    // link redirected to `/?acn=shop` from the lobby itself) never re-runs
    // ngOnInit, and the route's query-param streams stay silent when the
    // redirect lands on the URL the lobby is already showing, so later
    // arrivals are dispatched from the completed navigations instead. The
    // navigation that created this component is left to ngOnInit.
    const creatingNavigationId = this._router.getCurrentNavigation()?.id ?? null;
    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd && event.id !== creatingNavigationId),
        map((event) => this._router.parseUrl(event.urlAfterRedirects).queryParams['acn'] as string | undefined),
        filter((actionRef): actionRef is string => !!actionRef),
        takeUntilDestroyed(),
      )
      .subscribe((actionRef) => void this._dispatchAcn(actionRef));

    effect(() => {
      const pendingId = this._pendingFragment();
      const collections = this.allGameCollections();
      if (!pendingId || !collections?.length) return;
      if (collections.some((c) => c.game_collection_id === pendingId)) {
        queueMicrotask(() => this.scrollToCollection(pendingId));
        untracked(() => this._pendingFragment.set(null));
      }
    });

    effect(() => {
      const activeId = this.selectedCollectionId();
      if (!activeId || !this._isBrowser) return;

      setTimeout(() => {
        const navEl = this.quickNav()?.nativeElement ?? this._document.querySelector('.collection-quick-nav');
        if (!navEl) return;

        const activeTab = navEl.querySelector(`ac-single-select[data-id="${activeId}"]`) as HTMLElement | null;
        if (!activeTab) return;

        // navEl must be the overflow-x container; use scrollTo instead of scrollIntoView to avoid scrolling ancestor containers
        const navRect = navEl.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();
        const tabCenter = tabRect.left + tabRect.width / 2;
        const navCenter = navRect.left + navRect.width / 2;

        if (Math.abs(tabCenter - navCenter) > PILL_SCROLL_THRESHOLD_PX) {
          navEl.scrollTo({
            left: navEl.scrollLeft + (tabCenter - navCenter),
            behavior: 'smooth',
          });
        }
      });
    });

    afterNextRender(() => {
      this._initScrollSpy();
      this._maybePromptDownloadAppFromDeepLink();
    });

    const fragment = this._route.snapshot.fragment;
    if (fragment?.startsWith(HomeComponent.COLLECTION_FRAGMENT_PREFIX)) {
      this._pendingFragment.set(fragment.slice(HomeComponent.COLLECTION_FRAGMENT_PREFIX.length));
    }
  }

  async ngOnInit() {
    this._resumePendingAcnAfterAuth();
    await this._handleReferredLink();
  }

  openNoPurchaseNecessary = () => {
    this._dialog.open(NoPurchaseNecessaryDialogComponent);
  };

  /**
   * Desktop visitors who tap the download hero card get a QR of this page with
   * `?acn=download_app`. Scanning it reloads the page on their phone, where we
   * re-run the hero-card install flow so they land in the native CTA (iOS
   * OneLink / Android sideload). Guarded to real mobile platforms — a desktop
   * opening this link would otherwise just reopen the QR dialog. The
   * `hero_card_download_qr` campaign keeps these scan-driven installs
   * measurable apart from direct mobile taps. Runs inside `afterNextRender`,
   * so it is browser-only and needs no SSR guard.
   */
  private _maybePromptDownloadAppFromDeepLink() {
    if (this._route.snapshot.queryParams['acn'] !== DOWNLOAD_APP_DEEP_LINK_ACN) return;
    if (this._appInstall.detectPlatform() === null) return;
    this._appInstall.promptInstall('hero_card_download_qr');
  }

  private _initScrollSpy() {
    const scrollContainer = this._document.querySelector('.sidenav-content-container');
    if (!scrollContainer) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        this._updateActiveCollection(scrollContainer);
        ticking = false;
      });
    };

    scrollContainer.addEventListener('scroll', onScroll, { passive: true });
    this._destroyRef.onDestroy(() => {
      scrollContainer.removeEventListener('scroll', onScroll);
    });
  }

  private _updateActiveCollection(scrollContainer: Element) {
    if (this._isScrollingProgrammatically) return;

    const collections = this.allGameCollections();
    if (!collections?.length) return;

    const stickyNav = this._document.querySelector('.sticky-nav');
    const referenceTop = stickyNav
      ? stickyNav.getBoundingClientRect().bottom
      : scrollContainer.getBoundingClientRect().top;

    let activeId: string | null = null;
    for (const collection of collections) {
      const el = this._document.getElementById(
        HomeComponent.COLLECTION_FRAGMENT_PREFIX + collection.game_collection_id,
      );
      if (!el) continue;
      if (el.getBoundingClientRect().top <= referenceTop + 50) {
        activeId = collection.game_collection_id;
      }
    }

    if (activeId !== this.selectedCollectionId()) {
      this._selectedCollectionId.set(activeId);
    }
  }

  /**
   * Entry point for the "verify your identity" email, whose CTA lands here
   * with `acn=kyc`. The player's crypto purchase is already held pending
   * verification; the server releases and credits it once KYC is approved, so
   * all this has to do is get them through the dialog and tell them it worked.
   */
  private async _startKycFromEmailLink() {
    if (await this._auth.kycVerified) {
      return;
    }

    const result = await this.walletDialog.openKycDialog({ autoClose: true });

    if (result === 'verified') {
      this._alert.notifySuccess(
        'Identity verified',
        'Your held purchase will be credited to your account shortly.'
      );
    }
  }

  private _runAcnAction(actionRef: string) {
    switch (actionRef) {
      case 'purchase':
        this.walletDialog.openPurchaseModal().then();
        break;
      case 'redemption':
        // Delay otherwise the custom claim doesn't load causing it to
        // skip document verification
        setTimeout(() => this.walletDialog.startRedemptionFlow().then(), 250);
        break;
      case 'kyc':
        // Same claim-loading delay as the redemption case above.
        setTimeout(() => this._startKycFromEmailLink().then(), 250);
        break;
      case 'shop':
        this.merchDialog.openShop().then();
        break;
      default:
        break;
    }
  }

  /**
   * Same split as `_handleReferredLink`'s acn handling, for an action that
   * arrives after init: a signed-in player gets it right away, a signed-out
   * visitor gets the login prompt with the action stashed for replay.
   */
  private async _dispatchAcn(actionRef: string) {
    if (await this._auth.userP) {
      this._localStorage.removeItem(LocalStorageKey.GainsAcn);
      this._runAcnAction(actionRef);
      return;
    }

    this._localStorage.setItem(LocalStorageKey.GainsAcn, { acn: actionRef });
    this._dialog.open<AuthComponent, AuthDialogData>(AuthComponent, {
      id: DialogKey.Auth,
      data: { initialView: 'login' },
      disableClose: true,
    });
  }

  /**
   * Replays an `acn` the visitor arrived with while signed out, once they sign
   * in. Email CTAs land here on whatever device opened the mail, which is often
   * one without a session — dropping the action there would strand the player
   * on the lobby with no idea why they followed the link. Consumed exactly once:
   * the read clears it, so a later visit does not reopen the dialog. Every
   * signed-out to signed-in transition is watched, not just the first: the
   * lobby stays alive across a sign-out, and an action stashed after that must
   * still replay on the next sign-in rather than on some later page load.
   */
  private _resumePendingAcnAfterAuth() {
    this._auth.user$
      .pipe(
        map((user) => !!user),
        distinctUntilChanged(),
        filter(Boolean),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => {
        const pending = this._localStorage.getItem<{ acn: string }>(LocalStorageKey.GainsAcn);

        if (!pending?.acn) {
          return;
        }

        this._localStorage.removeItem(LocalStorageKey.GainsAcn);
        this._runAcnAction(pending.acn);
      });
  }

  private async _handleReferredLink() {
    const referral = this._route.snapshot.queryParams['ref'];
    const cvRef = this._route.snapshot.queryParams['cvref'];
    const actionRef = this._route.snapshot.queryParams['acn'];
    const user = await this._auth.userP;

    // Only put stuff that applies if user is signed in already
    if (user) {
      if (actionRef) {
        // The query param wins, so a re-init that still carries `acn` in the URL
        // cannot also replay the stored copy and open the dialog twice.
        this._localStorage.removeItem(LocalStorageKey.GainsAcn);
        this._runAcnAction(actionRef);
      }
    }

    // Only put stuff that applies if user is not signed in
    if (!user) {
      const openSignup = !!referral || !!cvRef;

      if (referral) {
        this._localStorage.setItem(LocalStorageKey.GainsReferrerUsername, {
          referral,
        });
      }

      if (cvRef) {
        this._localStorage.setItem(LocalStorageKey.ConversionSourceRef, {
          cvref: cvRef,
        });
      }

      if (actionRef) {
        this._localStorage.setItem(LocalStorageKey.GainsAcn, { acn: actionRef });
      }

      if (openSignup || actionRef) {
        this._dialog.open<AuthComponent, AuthDialogData>(AuthComponent, {
          id: DialogKey.Auth,
          // An `acn` on its own means an existing player following an email CTA,
          // so send them to login; a referral still means a new player.
          data: { initialView: openSignup ? 'signup' : 'login' },
          disableClose: true,
        });
      }
    }
  }
}
